
import { toast } from "sonner";

interface ResultsFiltersProps {
  vacations: any[];
  filteredVacations: any[];
  isFiltered: boolean;
  onFilteredVacationsChange: (vacations: any[]) => void;
  onIsFilteredChange: (isFiltered: boolean) => void;
  onDisplayCountChange: (count: number) => void;
}

export const ResultsFilters = ({ 
  vacations, 
  filteredVacations, 
  isFiltered, 
  onFilteredVacationsChange, 
  onIsFilteredChange, 
  onDisplayCountChange 
}: ResultsFiltersProps) => {
  
  const applyFilter = (filterText: string) => {
    console.log('ResultsFilters: Applying filter:', filterText, 'to', vacations.length, 'vacations');
    const lowerFilter = filterText.toLowerCase().trim();
    
    // Debug: Log some sample vacation data
    if (vacations.length > 0) {
      console.log('ResultsFilters: Sample vacation data:', {
        name: vacations[0].name,
        facilities: vacations[0].facilities,
        description: vacations[0].description?.substring(0, 100) + '...'
      });
    }
    
    // Start filtering from original vacations array
    const filtered = vacations.filter(vacation => {
      // Combine all searchable text
      const searchableText = [
        vacation.name || '',
        vacation.description || '',
        vacation.descriptionLong || '',
        vacation.facilities || '',
        vacation.extraInfo || '',
        vacation.accommodationType || '',
        vacation.country || '',
        vacation.city || '',
        vacation.region || '',
        vacation.specialCategories || '',
        vacation.typeVakantie || '',
        vacation.typeAccommodatie || ''
      ].join(' ').toLowerCase();

      // Debug for first few vacations
      if (vacations.indexOf(vacation) < 2) {
        console.log(`ResultsFilters: Checking vacation "${vacation.name}" with filter "${lowerFilter}"`);
        console.log(`  Combined text: ${searchableText.substring(0, 200)}...`);
      }

      // Specific filter patterns
      if (lowerFilter.includes('zwembad') || lowerFilter.includes('pool')) {
        return searchableText.includes('zwembad') || 
               searchableText.includes('pool') ||
               searchableText.includes('swimming');
      }
      
      if (lowerFilter.includes('all inclusive') || lowerFilter.includes('ai')) {
        return searchableText.includes('all inclusive') || 
               searchableText.includes('all-inclusive') ||
               searchableText.includes('allinclusive') ||
               searchableText.includes('alles inbegrepen') ||
               searchableText.includes('volledig verzorgd');
      }
      
      if (lowerFilter.includes('wellness') || lowerFilter.includes('spa')) {
        return searchableText.includes('wellness') || 
               searchableText.includes('spa') || 
               searchableText.includes('sauna') ||
               searchableText.includes('massage');
      }
      
      if (lowerFilter.includes('familie') || lowerFilter.includes('kinderen') || lowerFilter.includes('families')) {
        return searchableText.includes('familie') || 
               searchableText.includes('kinderen') ||
               searchableText.includes('families') ||
               searchableText.includes('kids') ||
               searchableText.includes('gezin') ||
               searchableText.includes('kindvriendelijk') ||
               searchableText.includes('kinderclub');
      }

      if (lowerFilter.includes('adults only') || lowerFilter.includes('volwassenen')) {
        return searchableText.includes('adults only') || 
               searchableText.includes('volwassenen') ||
               searchableText.includes('18+');
      }

      if (lowerFilter.includes('restaurant') || lowerFilter.includes('eten')) {
        return searchableText.includes('restaurant') || 
               searchableText.includes('dining') ||
               searchableText.includes('eten');
      }

      if (lowerFilter.includes('bar') || lowerFilter.includes('drankje')) {
        return searchableText.includes('bar') || 
               searchableText.includes('lounge');
      }
      
      // Price filtering - recognize various formats
      if (lowerFilter.includes('€') || 
          lowerFilter.includes('maximaal') || 
          lowerFilter.includes('max') || 
          lowerFilter.includes('onder') || 
          lowerFilter.includes('tot') ||
          lowerFilter.includes('budget') ||
          /\b\d+\s*(euro|eur|pp|per persoon)\b/.test(lowerFilter)) {
        
        let maxPrice = null;
        
        // Extract price from different patterns
        const pricePatterns = [
          /€(\d+)/,
          /(\d+)€/,
          /(\d+)\s*(euro|eur)/,
          /(maximaal|max|onder|tot|budget)\s*€?(\d+)/,
          /(\d+)\s*(pp|per persoon)/,
          /\b(\d+)\b/
        ];
        
        for (const pattern of pricePatterns) {
          const match = lowerFilter.match(pattern);
          if (match) {
            maxPrice = parseInt(match[2] || match[1]);
            if (maxPrice) break;
          }
        }
        
        if (maxPrice && vacation.price && vacation.price > 0) {
          return vacation.price <= maxPrice;
        }
      }
      
      // General text search
      return searchableText.includes(lowerFilter);
    });

    console.log('ResultsFilters: Filter applied, found', filtered.length, 'results');
    
    onFilteredVacationsChange(filtered);
    onIsFilteredChange(true);
    onDisplayCountChange(20); // Reset display count
    
    return filtered.length;
  };

  const resetFilters = () => {
    console.log('ResultsFilters: Resetting filters to', vacations.length, 'original vacations');
    onFilteredVacationsChange(vacations);
    onIsFilteredChange(false);
    onDisplayCountChange(20);
  };

  return {
    applyFilter,
    resetFilters
  };
};
